import { BN } from '@apps-orangefi/lib'
import { fetchClaimableStrykeRewards } from '@apps-orangefi/lib/api'
import { getUserPositionsQuery } from '@apps-orangefi/lib/subgraph/queries'
import { VaultReward, ProductCardProps } from '@apps-orangefi/lib/types'
import { convertVaultPositionList } from '@apps-orangefi/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { chain, cloneDeep } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useQuery as useUrqlQuery } from 'urql'

export type ClaimParams = {
  vaultAddressList: AddressType[]
  rewardTokenAddressList: AddressType[]
  amounts: BN[]
  merklProofsList: AddressType[][]
}

type ResultClaimableStrykeRewards = {
  vaultRewards: VaultReward[]
  totalClaimableAmountUsd: BN
  claimParams: ClaimParams
  isFetching: boolean
  refetch: () => void
}

const defaultClaimParams: ClaimParams = {
  vaultAddressList: [],
  rewardTokenAddressList: [],
  amounts: [],
  merklProofsList: [],
}

type IReward = {
  tokenAddress: AddressType
  symbol: string
  priceUsd: BN
  rewardRate: number
  iconUrl: any
}

export const useClaimableStrykeRewards = (
  account: AddressType | undefined,
  strykeRewardTokenAddress: AddressType,
  chainId: number,
  productList: ProductCardProps[],
  rewardTokenList: { vaultAddress: AddressType; rewardList: IReward[] }[]
): ResultClaimableStrykeRewards => {
  const [vaultRewards, setVaultRewards] = useState<VaultReward[]>([])
  const [claimParams, setClaimParams] = useState<ClaimParams>(cloneDeep(defaultClaimParams))
  const [totalClaimableAmountUsd, setTotalClaimableAmountUsd] = useState<BN>(new BN(0))

  // While the POINT site was in operation, users who had locked their positions in POINT were exempt from rewards.
  // Since the POINT site is no longer in operation, this will be temporarily discontinued.

  const {
    data: dataStrykeRewards,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['strykeRewards', { user: account, token: strykeRewardTokenAddress, chainId }],
    queryFn: fetchClaimableStrykeRewards,
    enabled: !!account && !!strykeRewardTokenAddress && !!chainId,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    let _totalClaimableAmountUsd = new BN(0)

    const _vaultRewards = chain(dataStrykeRewards)
      .map(strykeReward => {
        const vault = productList.find(
          item => item.vaultAddress.toLowerCase() === strykeReward.vault.toLowerCase()
        )

        if (!vault) {
          return
        }
        if (strykeReward.claimable.eq(0) && !vault.myPosition?.gt(0)) {
          return
        }
        const targetRewardToken = rewardTokenList.find(
          rewardToken => rewardToken.vaultAddress.toLowerCase() === vault.vaultAddress.toLowerCase()
        )

        const rewards =
          targetRewardToken?.rewardList.map(reward => {
            const amount = strykeReward.claimable.multipliedBy(reward.rewardRate)
            const amountUsd = amount.multipliedBy(reward.priceUsd)
            return {
              ...reward,
              amount,
              amountUsd,
            }
          }) ?? []

        const subTotalAmountUsd = rewards.reduce(
          (acc, reward) => acc.plus(reward.amountUsd),
          new BN(0)
        )

        _totalClaimableAmountUsd = _totalClaimableAmountUsd.plus(subTotalAmountUsd ?? 0)

        return {
          vaultAddress: vault.vaultAddress as AddressType,
          pairName: vault.title,
          baseTokenSymbol: vault.baseToken.symbol,
          quoteTokenSymbol: vault.quoteToken.symbol,
          amm: vault.platform?.amm,
          derivative: vault.platform?.derivative,
          rewards,
          claim: {
            rewardTokenAddress: strykeReward.token as AddressType,
            amount: strykeReward.claimable,
            proofs: strykeReward.proofs as AddressType[],
          },
        }
      })
      .compact()
      .value()

    const _claimParams = _vaultRewards.reduce((acc, reward) => {
      acc.vaultAddressList.push(reward.vaultAddress)
      acc.rewardTokenAddressList.push(reward.claim.rewardTokenAddress)
      acc.amounts.push(reward.claim.amount)
      acc.merklProofsList.push(reward.claim.proofs)
      return acc
    }, cloneDeep(defaultClaimParams))

    setVaultRewards(_vaultRewards)
    setTotalClaimableAmountUsd(_totalClaimableAmountUsd)
    setClaimParams(_claimParams)
  }, [
    JSON.stringify(productList),
    JSON.stringify(dataStrykeRewards),
    JSON.stringify(rewardTokenList),
  ])

  return {
    vaultRewards,
    totalClaimableAmountUsd,
    claimParams,
    isFetching,
    refetch,
  }
}
